// FILE: client/src/pages/secure/EscrowAccountDetailPage.js
// (This file is updated to fix the rendering loop and remove the icon dependency)

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useApiClient } from '../../hooks/useApiClient';
import { getCurrentUser } from '../../hooks/useCurrentUser';
import ReconciliationPanel from '../../components/secure/ReconciliationPanel';
import { printJournal } from '../../utils/escrowPrint';
import DataList from '../../components/shared/DataList';

// --- Add Journal Entry Modal ---
const AddJournalEntryModal = ({ isOpen, onClose, onEntryAdded, accountId }) => {
    const [formData, setFormData] = useState({ transaction_id: '', entry_type: 'Deposit', funds_type: 'earnest_money', entry_date: '', payee_or_payor: '', amount: '', check_number: '' });
    const [transactions, setTransactions] = useState([]);
    const apiClient = useApiClient();

    useEffect(() => {
        if (isOpen) {
            apiClient.get('/api/transactions').then(response => {
                if(Array.isArray(response)) {
                    setTransactions(response);
                }
            });
            setFormData({
                transaction_id: '',
                entry_type: 'Deposit',
                funds_type: 'earnest_money',
                entry_date: new Date().toISOString().split('T')[0],
                payee_or_payor: '',
                amount: '',
                check_number: ''
            });
        }
    }, [isOpen, apiClient]);

    const handleChange = (e) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            await apiClient.post('/api/escrow/journal', { ...formData, account_id: accountId });
            onEntryAdded();
            onClose();
        } catch (error) {
            console.error("Failed to add journal entry", error);
            alert(error.response?.data?.message || 'Failed to add entry.');
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
            <div className="bg-white p-8 rounded-lg shadow-xl w-full max-w-xl">
                <h2 className="text-2xl font-bold mb-6">Add New Journal Entry</h2>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <select name="transaction_id" value={formData.transaction_id} onChange={handleChange} required className="p-2 border rounded w-full"><option value="">Select Transaction</option>{transactions.map(t => <option key={t.transaction_id} value={t.transaction_id}>{t.street_address}</option>)}</select>
                    <select name="entry_type" value={formData.entry_type} onChange={handleChange} className="p-2 border rounded w-full"><option>Deposit</option><option>Disbursement</option></select>
                    <select name="funds_type" value={formData.funds_type} onChange={handleChange} className="p-2 border rounded w-full" title="Decides which ledger the entry is filed on">
                        <option value="earnest_money">Earnest money</option>
                        <option value="security_deposit">Security deposit</option>
                        <option value="rent">Rent</option>
                        <option value="broker_service_charge">Broker funds (service charge cushion)</option>
                        <option value="other">Other</option>
                    </select>
                    <input name="entry_date" type="date" value={formData.entry_date} onChange={handleChange} required className="p-2 border rounded w-full" />
                    <input name="payee_or_payor" value={formData.payee_or_payor} onChange={handleChange} placeholder="Payor/Payee" required className="p-2 border rounded w-full" />
                    <input name="amount" type="number" step="0.01" value={formData.amount} onChange={handleChange} placeholder="Amount" required className="p-2 border rounded w-full" />
                    <input name="check_number" value={formData.check_number} onChange={handleChange} placeholder="Check Number" className="p-2 border rounded w-full" />
                    <div className="flex justify-end gap-4"><button type="button" onClick={onClose} className="px-4 py-2 bg-gray-200 rounded">Cancel</button><button type="submit" className="px-4 py-2 bg-slate-800 text-white rounded">Save Entry</button></div>
                </form>
            </div>
        </div>
    );
};

// --- Correction Modal ---
const CorrectionModal = ({ isOpen, onClose, onCorrectionMade, entryToCorrect }) => {
    const [formData, setFormData] = useState({});
    const [transactions, setTransactions] = useState([]);
    const apiClient = useApiClient();

    useEffect(() => {
        if (isOpen && entryToCorrect) {
            apiClient.get('/api/transactions').then(response => {
                 if(Array.isArray(response)) {
                    setTransactions(response);
                }
            });
            setFormData({
                ...entryToCorrect,
                entry_date: new Date(entryToCorrect.entry_date).toISOString().split('T')[0]
            });
        }
    }, [isOpen, entryToCorrect, apiClient]);

    const handleChange = (e) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (window.confirm('This will post a reversing entry and a new corrected entry. This action cannot be undone. Proceed?')) {
            try {
                await apiClient.post('/api/escrow/journal/correct', {
                    original_entry_id: entryToCorrect.entry_id,
                    correction_data: formData
                });
                onCorrectionMade();
                onClose();
            } catch (error) {
                console.error("Failed to correct journal entry", error);
                alert(error.response?.data?.message || 'Failed to post correction.');
            }
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
            <div className="bg-white p-8 rounded-lg shadow-xl w-full max-w-xl">
                <h2 className="text-2xl font-bold mb-2">Correct Journal Entry #{entryToCorrect.entry_id}</h2>
                <p className="mb-6 text-sm text-slate-600">You are correcting the original entry. A reversal will be posted automatically.</p>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <select name="transaction_id" value={formData.transaction_id} onChange={handleChange} required className="p-2 border rounded w-full"><option value="">Select Transaction</option>{transactions.map(t => <option key={t.transaction_id} value={t.transaction_id}>{t.street_address}</option>)}</select>
                    <select name="entry_type" value={formData.entry_type} onChange={handleChange} className="p-2 border rounded w-full"><option>Deposit</option><option>Disbursement</option></select>
                    <input name="entry_date" type="date" value={formData.entry_date} onChange={handleChange} required className="p-2 border rounded w-full" />
                    <input name="payee_or_payor" value={formData.payee_or_payor} onChange={handleChange} placeholder="Payor/Payee" required className="p-2 border rounded w-full" />
                    <input name="amount" type="number" step="0.01" value={formData.amount} onChange={handleChange} placeholder="Amount" required className="p-2 border rounded w-full" />
                    <input name="check_number" value={formData.check_number} onChange={handleChange} placeholder="Check Number" className="p-2 border rounded w-full" />
                    <div className="flex justify-end gap-4 pt-4"><button type="button" onClick={onClose} className="px-4 py-2 bg-gray-200 rounded">Cancel</button><button type="submit" className="px-4 py-2 bg-green-600 text-white rounded">Post Correction</button></div>
                </form>
            </div>
        </div>
    );
};


// --- Main Detail Page Component ---
const EscrowAccountDetailPage = () => {
    const { id } = useParams();
    const [account, setAccount] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isCorrectionModalOpen, setIsCorrectionModalOpen] = useState(false);
    const [entryToCorrect, setEntryToCorrect] = useState(null);
    const [printFrom, setPrintFrom] = useState('');
    const [printTo, setPrintTo] = useState('');
    const [printError, setPrintError] = useState('');
    const apiClient = useApiClient();

    // Optimized data fetching to prevent re-render loops
    const fetchAccountData = useCallback(async () => {
        setIsLoading(true);
        try {
            const data = await apiClient.get(`/api/escrow/accounts/${id}`);
            setAccount(data);
        } catch (err) {
            setError(err.message || 'Failed to fetch account details.');
        } finally {
            setIsLoading(false);
        }
    }, [id, apiClient]); // Dependencies are stable and won't change on re-renders

    useEffect(() => {
        fetchAccountData();
    }, [fetchAccountData]); // This effect now runs only when the component mounts or id/apiClient changes

    const handleOpenCorrectionModal = (entry) => {
        setEntryToCorrect(entry);
        setIsCorrectionModalOpen(true);
    };

    const formatCurrency = (amount) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
    const printJournalReport = async () => {
        setPrintError('');
        try {
            const q = new URLSearchParams({ account_id: id, ...(printFrom ? { from: printFrom } : {}), ...(printTo ? { to: printTo } : {}) });
            printJournal(await apiClient.get(`/api/escrow/journal-report?${q}`));
        } catch (err) {
            setPrintError(err.message || 'Could not build the journal.');
        }
    };

    const currentUser = getCurrentUser();
    const canReconcile = !!(currentUser?.isManagingBroker || currentUser?.staffRole === 'Bookkeeper');

    if (isLoading) return <div className="p-8">Loading account details...</div>;
    if (error) return <div className="p-8 text-red-500">{error}</div>;
    if (!account) return null;

    return (
        <div className="p-4 md:p-8">
            <AddJournalEntryModal isOpen={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} onEntryAdded={fetchAccountData} accountId={id} />
            <CorrectionModal isOpen={isCorrectionModalOpen} onClose={() => setIsCorrectionModalOpen(false)} onCorrectionMade={fetchAccountData} entryToCorrect={entryToCorrect} />

            <div>
                <Link to="/dashboard/escrow" className="text-indigo-600 hover:underline mb-6 inline-block">&larr; Back to Escrow Portal</Link>
                <div className="flex justify-between items-center my-6">
                    <div>
                        <h1 className="text-3xl font-bold">{account.account_name}</h1>
                        <p className="text-slate-600">{account.bank_name} - Acct #: {account.account_number}</p>
                    </div>
                    <button onClick={() => setIsAddModalOpen(true)} className="px-4 py-2 text-sm text-white bg-slate-800 rounded-md hover:bg-slate-900">Add New Entry</button>
                </div>

                <div className="bg-white p-8 rounded-lg shadow-lg">
                    <div className="flex flex-wrap justify-between items-end gap-4 mb-4">
                        <h2 className="text-2xl font-bold">Account Journal</h2>
                        <div className="flex flex-wrap items-end gap-2 text-sm">
                            <label className="flex flex-col text-xs text-slate-500">From
                                <input type="date" value={printFrom} onChange={e => setPrintFrom(e.target.value)} className="p-1 border rounded text-sm text-slate-800" />
                            </label>
                            <label className="flex flex-col text-xs text-slate-500">To
                                <input type="date" value={printTo} onChange={e => setPrintTo(e.target.value)} className="p-1 border rounded text-sm text-slate-800" />
                            </label>
                            <button onClick={printJournalReport} className="px-3 py-2 bg-slate-800 text-white rounded hover:bg-slate-700"
                                title="The journal in the column format IDFPR accepted. Keep a printed copy at the office for 60 days.">
                                Print journal
                            </button>
                        </div>
                    </div>
                    {printError && <p className="text-sm text-red-600 mb-2">{printError}</p>}
                    <DataList rows={account.journal || []} rowKey="entry_id" csvName={`escrow-journal-${id}`}
                        defaultSort={{ key: 'entry_date', dir: 'desc' }} empty="No journal entries yet."
                        rowClass={(entry) => (entry.memo ? 'bg-yellow-50' : '')}
                        filters={[{ key: 'entry_type', label: 'Type' }, { key: 'cleared', label: 'Cleared' }]}
                        columns={[
                            { key: 'entry_date', label: 'Date', type: 'date', value: (e) => (e.entry_date ? String(e.entry_date).slice(0, 10) : null) },
                            { key: 'ledger', label: 'Ledger', value: (e) => (e.ledger_id ? (e.ledger_description || e.street_address || `Ledger #${e.ledger_id}`) : (e.street_address || 'No ledger')),
                                render: (entry) => (entry.ledger_id
                                    ? <Link to={`/dashboard/escrow/ledgers/${entry.ledger_id}`} className="text-indigo-600 hover:underline">{entry.ledger_description || entry.street_address || `Ledger #${entry.ledger_id}`}</Link>
                                    : <span className="text-amber-700" title="Not on any ledger — 1450.750(i)(2)">{entry.street_address || 'No ledger'}</span>) },
                            { key: 'payee_or_payor', label: 'Payor/Payee', value: (e) => [e.payee_or_payor, e.memo].filter(Boolean).join(' · '),
                                render: (entry) => <>{entry.payee_or_payor}{entry.memo && <div className="text-xs text-slate-500 italic">{entry.memo}</div>}</> },
                            { key: 'entry_type', label: 'Type' },
                            { key: 'deposit', label: 'Deposit', type: 'number', value: (e) => (e.entry_type === 'Deposit' ? e.amount : null), render: (e) => <span className="text-green-600">{e.entry_type === 'Deposit' ? formatCurrency(e.amount) : ''}</span> },
                            { key: 'disbursement', label: 'Disbursement', type: 'number', value: (e) => (e.entry_type === 'Disbursement' ? e.amount : null), render: (e) => <span className="text-red-600">{e.entry_type === 'Disbursement' ? formatCurrency(e.amount) : ''}</span> },
                            { key: 'running_balance', label: 'Account Balance', type: 'number', render: (e) => <span className="font-semibold">{formatCurrency(e.running_balance)}</span> },
                            { key: 'cleared', label: 'Cleared', value: (e) => (e.cleared ? 'Cleared' : 'Outstanding'),
                                render: (entry) => (entry.cleared
                                    ? <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">Cleared</span>
                                    : <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-600">Outstanding</span>) },
                            { key: 'manage', label: 'Manage', sortable: false, csv: false, search: false, render: (entry) => (
                                <button onClick={() => handleOpenCorrectionModal(entry)} className="text-blue-600 hover:text-blue-800" title="Correct this entry">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                        <path d="M17.414 2.586a2 2 0 00-2.828 0L7 10.172V13h2.828l7.586-7.586a2 2 0 000-2.828z" />
                                        <path fillRule="evenodd" d="M2 6a2 2 0 012-2h4a1 1 0 010 2H4v10h10v-4a1 1 0 112 0v4a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" clipRule="evenodd" />
                                    </svg>
                                </button>) },
                        ]} />
                </div>

                {canReconcile && <ReconciliationPanel accountId={id} onReconciled={fetchAccountData} />}
            </div>
        </div>
    );
};

export default EscrowAccountDetailPage;
